



























































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ITaxTagLocation } from "@/models/tax";
import HasValidation from "@/mixins/HasValidation";
import CountriesStore from "@/mixins/constants/countriesStore";
import { mapState } from "vuex";
import { IState } from "@/store";
import { ICountry, IRegion } from "@/models/constants";
import { ActionTypes } from "@/data/enums";
import i18n from "@/i18n";
import { CreateElement } from "vue";

enum LocationGranularity {
  Country = "country",
  Region = "country/region"
}

function LocationGranularityMap() {
  return {
    [LocationGranularity.Country]: {
      key: LocationGranularity.Country,
      label: i18n.t("_.country")
    },
    [LocationGranularity.Region]: {
      key: LocationGranularity.Region,
      label: i18n.t("_.region")
    }
  };
}

@Component({
  components: {
    "region-check-list": () =>
      import("@/components/app/global/address/regionCheckList.vue")
  },
  computed: {
    ...mapState({
      brandCountryId: (state: IState) => state.brand.country_id,
      regions(state: IState, getters) {
        return getters[`constants/getRegions`](
          this.$_.get(this.country, "code")
        );
      }
    })
  }
})
export default class AddEditTaxLocationModal extends Mixins(
  HasValidation,
  CountriesStore
) {
  @Prop({ default: () => [] })
  public readonly taxLocations!: ITaxTagLocation[];

  @Prop({ default: ActionTypes.CREATE })
  public action!: ActionTypes;

  // Data
  public locationGranularityMap = LocationGranularityMap();
  public selectedGranularity = LocationGranularity.Country;
  public selectedCountryIds = [];

  public regions!: IRegion[];
  public brandCountryId!: ICountry["id"];
  public countryIds: ITaxTagLocation["country_id"][] = [];
  public regionIds: ITaxTagLocation["region_id"][] = [];
  public postCode = "";
  public isProcessing = false;
  public isLoadingRegions = false;

  // Lifecycle methods
  public async created() {
    this.selectedGranularity = this.computeGranularity();
    await this.loadRegions();
    this.countryIds = this.taxLocations.map(i => i.country_id);
    this.regionIds = this.taxLocations.map(i => i.region_id);
    this.setDefaultCountry();
  }

  // Computed

  public get isRegion() {
    return this.selectedGranularity === LocationGranularity.Region;
  }
  public get isCountry() {
    return this.selectedGranularity === LocationGranularity.Country;
  }

  public get isCountryRegionless() {
    return !this.regions?.length;
  }

  public get isValid() {
    if (this.hasErrors) return false;
    return true;
  }

  public get isCountrySelected() {
    return this.isRegion && this.countryIds[0];
  }

  public get country() {
    if (!this.isCountrySelected) return null;
    return this.$_.find(
      this.countries,
      country => country.id === this.countryIds[0]
    );
  }

  public get region() {
    if (!this.isCountrySelected) return null;
    return this.$_.find(
      this.regions,
      region => region.id === this.regionIds[0]
    );
  }

  public get i18n() {
    return {
      title: this.$t("_action.add_locations"),
      modalAction: this.$t("_action.save")
    };
  }

  public get countrySelectRenderer() {
    return {
      functional: true,
      render: (h: CreateElement) => {
        return h("country-select", {
          props: {
            value: this.isCountry ? this.countryIds : this.countryIds[0],
            disabled: this.isProcessing,
            isMultiselect: this.isCountry
          },
          attrs: { placeholder: this.$t("_.select_country") },
          on: {
            input: async val => {
              this.isCountry
                ? (this.countryIds = val)
                : (this.countryIds = [val]);
              this.loadRegions();
            }
          }
        });
      }
    };
  }

  public computeGranularity() {
    if (this.action === ActionTypes.CREATE) return LocationGranularity.Country;
    return this.taxLocations.find(i => i.region_id)
      ? LocationGranularity.Region
      : LocationGranularity.Country;
  }

  public get regionRenderer() {
    if (this.isCountryRegionless) return null;

    return {
      functional: true,
      render: (h: CreateElement) => {
        if (this.isRegion)
          return h("region-check-list", {
            props: {
              country: this.country,
              isDisabled: this.isLoadingRegions,

              value: this.regionIds
            },
            on: {
              input: val => {
                this.regionIds = val;
              }
            }
          });
        else return null;
      }
    };
  }

  public onGranularityChange() {
    this.postCode = "";
    this.countryIds = [];
    this.regionIds = [];
    this.loadRegions();
  }

  public loadRegions() {
    if (!this.isCountrySelected) return;
    this.isLoadingRegions = true;
    return this.$store
      .dispatch("constants/getRegions", {
        countryId: this.$_.get(this.country, "id"),
        countryCode: this.$_.get(this.country, "code")
      })
      .finally(() => (this.isLoadingRegions = false));
  }

  public setDefaultCountry() {
    if (!this.countryIds.length) {
      this.countryIds = [this.brandCountryId];
    }
  }

  public generateTaxLocations() {
    if (this.isCountry) {
      return this.countryIds?.map(country_id => {
        const country = this.countries.find(i => i.id === country_id);
        return {
          country_id,
          country,
          region_id: null,
          post_code: null
        } as ITaxTagLocation;
      });
    } else if (this.isRegion) {
      const countryId = this.countryIds?.[0];
      const country = this.countries.find(i => i.id === countryId);
      return this.regionIds?.map(region_id => {
        const region = this.regions.find(i => i.id === region_id);
        return {
          country_id: countryId,
          country,
          region_id,
          region,
          post_code: undefined
        } as ITaxTagLocation;
      });
    }
  }

  public async validateBeforeSubmit() {
    try {
      this.isProcessing = true;
      const result = await this.validate();
      if (result) {
        this.$emit("save", { taxLocations: this.generateTaxLocations() });
        this.$emit("close");
      } else {
        throw this.$t("_sentence.form_is_not_valid");
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }
}
